import { useEffect, useMemo, useRef, useState } from "react";
import { animate, useInView, useReducedMotion } from "framer-motion";
import { Star, Users, FolderCheck, CalendarClock } from "lucide-react";
import { getLiveStats } from "@/data/site";

const CountUp = ({ to, decimals = 0, suffix = "+" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "0px 0px -10% 0px" });
  const reduce = useReducedMotion();
  const [val, setVal] = useState(to);
  useEffect(() => {
    if (!inView || reduce) { setVal(to); return undefined; }
    const controls = animate(0, to, { duration: 1.4, ease: "easeOut", onUpdate: (v) => setVal(v) });
    return () => controls.stop();
  }, [inView, reduce, to]);
  return <span ref={ref}>{val.toFixed(decimals)}{suffix}</span>;
};

/**
 * Live statistics band. Values come from getLiveStats() so they grow automatically:
 * clients and projects +1 per week, experience +1 per year, rating fixed.
 */
export const StatsBand = ({ dark = true, className = "" }) => {
  const s = useMemo(() => getLiveStats(), []);
  const items = [
    { icon: Users, value: s.clients, label: "Happy clients", note: "Businesses served worldwide" },
    { icon: FolderCheck, value: s.projects, label: "Projects delivered", note: "Websites, apps and software" },
    { icon: Star, value: s.rating, decimals: 1, label: "Average rating", note: "From client feedback" },
    { icon: CalendarClock, value: s.years, label: "Years of experience", note: "Building for the web" },
  ];
  return (
    <section className={`${dark ? "bg-ink on-dark" : "bg-sand"} ${className}`} aria-label="Athlevo IT Solution in numbers" data-testid="stats-band">
      <div className="container-x py-12 md:py-16">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-10 lg:grid-cols-4">
          {items.map((it) => (
            <div key={it.label} className="flex flex-col" data-testid={`stat-${it.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <it.icon className={`h-5 w-5 ${dark ? "text-lime" : "text-sageDeep"}`} aria-hidden />
              <dd className={`order-1 mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl ${dark ? "text-white" : "text-ink"}`}>
                <CountUp to={it.value} decimals={it.decimals || 0} />
              </dd>
              <dt className={`order-2 mt-2 text-sm font-bold uppercase tracking-[0.14em] ${dark ? "text-lime" : "text-sageDeep"}`}>{it.label}</dt>
              <p className={`order-3 mt-1 text-sm ${dark ? "text-white/70" : "text-ink/70"}`}>{it.note}</p>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
};
